import { useCallback, useEffect, useState } from 'react'

export const TEXT_ZOOM_MIN = 0.6
export const TEXT_ZOOM_MAX = 2
const STEP = 0.1

export interface TextZoom {
  /** Multiplier applied to the rendered text, 1 = actual size. */
  zoom: number
  zoomIn: () => void
  zoomOut: () => void
  reset: () => void
  canZoomIn: boolean
  canZoomOut: boolean
}

const clamp = (value: number) =>
  Math.min(TEXT_ZOOM_MAX, Math.max(TEXT_ZOOM_MIN, Math.round(value * 10) / 10))

/**
 * Zoom for the renderers that show text — markdown, code, CSV, JSON (#755).
 *
 * A PDF brings its own page-aware zoom, so this is not used for it. The
 * value is meant for CSS `zoom` on the renderer's wrapper: the markdown and
 * code renderers set their own font sizes, so an inherited font-size would
 * reach nothing, and unlike a transform `zoom` keeps the inner scroll
 * container's geometry consistent. `resetKey` identifies the document — a
 * different document starts at actual size.
 */
export function useTextZoom(resetKey: string): TextZoom {
  const [zoom, setZoom] = useState(1)
  useEffect(() => setZoom(1), [resetKey])
  const zoomIn = useCallback(() => setZoom((z) => clamp(z + STEP)), [])
  const zoomOut = useCallback(() => setZoom((z) => clamp(z - STEP)), [])
  const reset = useCallback(() => setZoom(1), [])
  return {
    zoom,
    zoomIn,
    zoomOut,
    reset,
    canZoomIn: zoom < TEXT_ZOOM_MAX,
    canZoomOut: zoom > TEXT_ZOOM_MIN,
  }
}
